/**
 * Split pasted identifiers on new lines, commas and tabs (a spreadsheet column or
 * row). Values stay strings: trimmed only — leading zeros and case are kept, so
 * "00152" never becomes "152".
 */
export function parseIdentifiers(text: string): string[] {
  return text.split(/[\n\r,\t]+/).map((v) => v.trim()).filter((v) => v.length > 0)
}

/**
 * How text separates into 2+ values: 'lines' = a newline or tab (a pasted
 * column or row: always a list); 'commas' = commas only (ambiguous: "Smith,
 * John" or "E11.9, I10" are one query); null = one value.
 */
export function listKind(text: string): 'lines' | 'commas' | null {
  if (parseIdentifiers(text).length < 2) return null
  return /[\n\r\t]/.test(text) ? 'lines' : 'commas'
}

/** The user chose "Search as N IDs" for comma-separated text that did not read as IDs. */
export type CommaAs = 'ids' | null

/**
 * The search panel's mode switch. A newline or tab with 2+ values is a list.
 * Commas alone make a list only when every part looks like an identifier — no
 * internal spaces and at least one digit: "C-1, C-2" and "E11.9, I10" are lists;
 * "Smith, John" and "Acme Health, LLC" stay one query, which the user can still search
 * as IDs (`commaAs`).
 */
export function isIdentifierList(text: string, commaAs: CommaAs = null): boolean {
  const kind = listKind(text)
  if (kind !== 'commas') return kind === 'lines'
  if (commaAs) return true
  return parseIdentifiers(text).every((v) => !/\s/.test(v) && /\d/.test(v))
}

/** The single-mode query: trimmed, without trailing commas ("15250," searches "15250"). */
export const singleQuery = (text: string) => text.trim().replace(/[,\s]+$/, '')

/**
 * Distinct values, first occurrence order — what the search actually runs.
 * Matching is case-insensitive for every field (claim / Patient ID blind indexes
 * fold case; med_ids are generated lowercase), so duplicates collapse on a
 * lowercased key and the first-seen spelling is kept — the backend does the same.
 * med_ids are sent lowercased, as the backend compares them.
 */
export function uniqueIdentifiers(values: string[], field?: string): string[] {
  const byKey = new Map<string, string>()
  for (const v of values) {
    const key = v.toLowerCase()
    if (!byKey.has(key)) byKey.set(key, field === 'med_id' ? key : v)
  }
  return [...byKey.values()]
}

/** Longest value the backend accepts (search.routes.ts advancedSearchSchema). */
export const MAX_VALUE_LENGTH = 100
